import React, { useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import CardIcon from '../icons/CardIcon';

function createCroppedFile(imageUrl, cropArea, sourceFile) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(cropArea.width);
      canvas.height = Math.round(cropArea.height);
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('Unable to prepare this photo.'));
        return;
      }

      context.drawImage(
        image,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Unable to crop this photo.'));
          return;
        }

        const fileName = `${sourceFile.name.replace(/\.[^.]+$/, '') || 'profile-photo'}.jpg`;
        resolve(new File([blob], fileName, { type: 'image/jpeg', lastModified: Date.now() }));
      }, 'image/jpeg', 0.92);
    };
    image.onerror = () => reject(new Error('Unable to read this photo.'));
    image.src = imageUrl;
  });
}

function ProfilePhotoCropper({ file, onCancel, onCrop, isProcessing = false, error = '' }) {
  const [imageUrl, setImageUrl] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState(null);
  const [cropError, setCropError] = useState('');

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleSave = async () => {
    if (!cropArea) return;
    setCropError('');
    try {
      const croppedFile = await createCroppedFile(imageUrl, cropArea, file);
      await onCrop(croppedFile);
    } catch (cropFailure) {
      setCropError(cropFailure.message || 'Unable to crop this photo.');
    }
  };

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/60 p-4" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isProcessing) onCancel();
    }}>
      <section className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="profile-photo-crop-title">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 id="profile-photo-crop-title" className="text-lg font-bold text-navy">Adjust profile photo</h2>
            <p className="mt-1 text-sm text-slate-500">Drag to position the visible area.</p>
          </div>
          <button type="button" onClick={onCancel} disabled={isProcessing} aria-label="Cancel photo crop" className="rounded-full p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50">
            <CardIcon name="x" className="h-5 w-5" />
          </button>
        </header>

        <div className="relative mx-auto aspect-square w-full max-w-md bg-slate-950 sm:my-5 sm:rounded-xl">
          {imageUrl && <Cropper image={imageUrl} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, pixels) => setCropArea(pixels)} />}
        </div>

        <div className="space-y-4 px-5 pb-5 pt-4">
          <label className="block text-sm font-medium text-slate-700">
            Zoom
            <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} disabled={isProcessing} className="mt-2 block w-full accent-orange-600" />
          </label>
          {(cropError || error) && <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{cropError || error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onCancel} disabled={isProcessing} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={!cropArea || isProcessing} className="inline-flex items-center gap-2 rounded-xl bg-navy px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
              <CardIcon name="check" />{isProcessing ? 'Uploading...' : 'Use this photo'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ProfilePhotoCropper;